import { useEffect } from 'react';

const BREADCRUMB_LABELS = {
  about: 'About',
  services: 'Services',
  portfolio: 'Portfolio',
  contact: 'Contact',
  blog: 'Blog',
  founder: 'Founder',
  'book-consultation': 'Book Consultation',
  'student-projects': 'Student Projects',
  'privacy-policy': 'Privacy Policy',
  'cookie-policy': 'Cookie Policy',
  'terms-conditions': 'Terms & Conditions'
};

const useSEO = (title, description, keywords, canonicalUrl) => {
  useEffect(() => {
    // Set page title
    if (title) {
      document.title = `${title} | ZS NEXATECH`;
    }

    // Set meta description
    if (description) {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = description;
    }

    // Set meta keywords
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.name = 'keywords';
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.content = keywords;
    }

    // Set canonical URL
    if (canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = canonicalUrl;

      const existingBreadcrumbSchema = document.querySelector('script[data-seo="breadcrumb-schema"]');
      if (existingBreadcrumbSchema) {
        existingBreadcrumbSchema.remove();
      }

      try {
        const parsedUrl = new URL(canonicalUrl);
        const pathSegments = parsedUrl.pathname
          .split('/')
          .filter(Boolean);

        if (pathSegments.length > 0) {
          const breadcrumbItems = [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: `${parsedUrl.origin}/`
            }
          ];

          pathSegments.forEach((segment, index) => {
            const segmentPath = `/${pathSegments.slice(0, index + 1).join('/')}`;
            const segmentName = BREADCRUMB_LABELS[segment] || segment
              .split('-')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

            breadcrumbItems.push({
              '@type': 'ListItem',
              position: index + 2,
              name: segmentName,
              item: `${parsedUrl.origin}${segmentPath}`
            });
          });

          const breadcrumbSchema = {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems
          };

          const breadcrumbScript = document.createElement('script');
          breadcrumbScript.type = 'application/ld+json';
          breadcrumbScript.setAttribute('data-seo', 'breadcrumb-schema');
          breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
          document.head.appendChild(breadcrumbScript);
        }
      } catch (error) {
        console.error('Failed to create breadcrumb schema:', error);
      }
    }

    // Add Open Graph meta tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle && title) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.content = `${title} | ZS NEXATECH`;
      document.head.appendChild(meta);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription && description) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      meta.content = description;
      document.head.appendChild(meta);
    }

    // Scroll to top on page change
    window.scrollTo(0, 0);
  }, [title, description, keywords, canonicalUrl]);
};

export default useSEO;
